/* SoulBotanic — premium formula comparison. */
(function () {
  'use strict';

  /* "Different standard." types itself out when the headline scrolls into
     view. The em is rebuilt as an invisible ghost (which keeps the finished
     line's footprint, so the headline never reflows) plus a typed overlay;
     the full text stays in a visually-hidden span for assistive tech. The
     global sb-reveal observer force-reveals everything after 2.5s, so the
     trigger is a dedicated observer, not the is-in class. */
  function initTypewriter(root) {
    var em = root.querySelector('.sb-versus__title em');
    if (!em || em.children.length || em.dataset.sbTypeReady === '1') { return; }
    if (!window.matchMedia || !('IntersectionObserver' in window)) { return; }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { return; }

    var text = em.textContent;
    if (!text) { return; }
    em.dataset.sbTypeReady = '1';

    var srText = document.createElement('span');
    srText.className = 'visually-hidden';
    srText.textContent = text;

    var wrap = document.createElement('span');
    wrap.className = 'sb-versus__typewrap';
    wrap.setAttribute('aria-hidden', 'true');

    var ghost = document.createElement('span');
    ghost.className = 'sb-versus__type-ghost';
    ghost.textContent = text;

    var typed = document.createElement('span');
    typed.className = 'sb-versus__typed';

    wrap.appendChild(ghost);
    wrap.appendChild(typed);
    em.textContent = '';
    em.appendChild(srText);
    em.appendChild(wrap);

    var CHAR_MS = 70;
    /* Lets "Same shelf." land before the second line starts to write. */
    var START_DELAY_MS = 420;

    function type() {
      wrap.classList.add('is-typing');
      var index = 0;
      var tick = function () {
        index += 1;
        typed.textContent = text.slice(0, index);
        if (index < text.length) {
          window.setTimeout(tick, CHAR_MS);
        } else {
          wrap.classList.add('is-done');
        }
      };
      window.setTimeout(tick, START_DELAY_MS);
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) { return; }
        observer.disconnect();
        type();
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.5 });
    observer.observe(em);
  }

  function init(root) {
    if (!root || root.dataset.sbVersusReady === '1') { return; }

    initTypewriter(root);

    var media = root.querySelector('[data-versus-media]');
    var pan = root.querySelector('[data-versus-pan]');
    var labelPan = root.querySelector('[data-versus-label-pan]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-versus-pill]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-versus-panel]'));
    var previous = root.querySelector('[data-versus-prev]');
    var next = root.querySelector('[data-versus-next]');
    var status = root.querySelector('[data-versus-status]');
    var tablist = root.querySelector('[data-versus-tablist]');

    if (!media || !pan || !panels.length) { return; }
    root.dataset.sbVersusReady = '1';

    var panLayers = [pan];
    if (labelPan) { panLayers.push(labelPan); }
    var panBlur = root.querySelector('[data-versus-pan-blur]');
    if (panBlur) { panLayers.push(panBlur); }

    var count = panels.length;
    var activeIndex = 0;
    /* Per-product accents ride product-<handle> classes on the root (the §9
       theme tokens in design-tokens.css); a tab switch swaps the class. */
    var themeClasses = tabs.map(function (tab) {
      return tab.dataset.versusTheme || '';
    });
    var reducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var centers = (media.getAttribute('data-versus-centers') || '')
      .split(',')
      .map(function (value) { return parseFloat(value) / 100; })
      .filter(function (value) { return Number.isFinite(value); });

    if (centers.length !== count) { centers = []; }

    function clamp(value, minimum, maximum) {
      return Math.max(minimum, Math.min(maximum, value));
    }

    function centerFor(index) {
      return centers.length ? centers[index] : (index + 0.5) / count;
    }

    function positionPan() {
      var naturalWidth = pan.naturalWidth || parseFloat(pan.getAttribute('width')) || 0;
      var naturalHeight = pan.naturalHeight || parseFloat(pan.getAttribute('height')) || 0;
      var mediaHeight = media.clientHeight;

      if (!naturalWidth || !naturalHeight || !mediaHeight) { return; }

      var renderedWidth = mediaHeight * (naturalWidth / naturalHeight);
      var activeCenter = centerFor(activeIndex);
      var idealOffset = media.clientWidth / 2 - activeCenter * renderedWidth;
      var minimumOffset = Math.min(0, media.clientWidth - renderedWidth);
      /* Isolate the active station wherever the deck is STACKED (mobile
         AND the tablet band ≤1439px) — the wide short strip otherwise
         shows three products at once (owner report 2026-07-24). Keep in
         lock-step with the stacked-layout breakpoints in
         section-versus.css. */
      var isolateStation = window.matchMedia &&
        window.matchMedia('(max-width: 1439px)').matches;
      var offset = Math.max(minimumOffset, Math.min(0, idealOffset));
      var clipLeft = 0;
      var clipRight = 0;

      if (isolateStation) {
        var stationHalf = 0.5 / count;
        clipLeft = clamp(activeCenter - stationHalf, 0, 1) * 100;
        clipRight = (1 - clamp(activeCenter + stationHalf, 0, 1)) * 100;

        offset = idealOffset;
      }

      /* Where the media window's edges fall on the pan, in pan-%: the
         tablet band's feather mask fades the neighbouring stations out at
         exactly these bounds (the main jar stays centred between them). */
      var viewLeft = (-offset / renderedWidth) * 100;
      var viewRight = ((-offset + media.clientWidth) / renderedWidth) * 100;
      media.style.setProperty('--sb-versus-view-left', viewLeft.toFixed(2) + '%');
      media.style.setProperty('--sb-versus-view-right', viewRight.toFixed(2) + '%');

      panLayers.forEach(function (layer) {
        layer.style.setProperty('--sb-versus-clip-left', clipLeft.toFixed(3) + '%');
        layer.style.setProperty('--sb-versus-clip-right', clipRight.toFixed(3) + '%');
        if (layer === panBlur) {
          /* The blurred flanks render slightly SMALLER, scaled about the
             ACTIVE station's centre (origin in element px) so the
             crossfade seam stays registered; the scale factor comes from
             CSS (band-only) and is 1 elsewhere. */
          layer.style.transformOrigin = Math.round(activeCenter * renderedWidth) + 'px 85%';
          layer.style.transform = 'translateX(' + Math.round(offset) + 'px) scale(var(--sb-versus-flank-scale, 1))';
        } else {
          layer.style.transform = 'translateX(' + Math.round(offset) + 'px)';
        }
      });
    }

    /* The desktop rail's sliding selection pill (CSS ::before) reads its
       box from these vars — measured, so it works for the horizontal band
       and the vertical rail alike. offsets are relative to the positioned
       tablist (rail) itself. */
    function updateIndicator() {
      if (!tablist) { return; }
      var active = tabs[activeIndex];
      if (!active) { return; }
      tablist.style.setProperty('--sb-versus-ind-x', active.offsetLeft + 'px');
      tablist.style.setProperty('--sb-versus-ind-y', active.offsetTop + 'px');
      tablist.style.setProperty('--sb-versus-ind-w', active.offsetWidth + 'px');
      tablist.style.setProperty('--sb-versus-ind-h', active.offsetHeight + 'px');
    }

    function keepActiveTabVisible(tab) {
      if (!tab || !tab.parentElement) { return; }
      var rail = tab.parentElement;
      if (rail.scrollWidth <= rail.clientWidth + 1) { return; }

      var target = tab.offsetLeft - (rail.clientWidth - tab.offsetWidth) / 2;
      if (reducedMotion || typeof rail.scrollTo !== 'function') {
        rail.scrollLeft = target;
      } else {
        rail.scrollTo({ left: target, behavior: 'smooth' });
      }
    }

    function select(index, focusTab) {
      activeIndex = clamp(index, 0, count - 1);

      tabs.forEach(function (tab, tabIndex) {
        var selected = tabIndex === activeIndex;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');
        if (selected) {
          themeClasses.forEach(function (themeClass) {
            if (themeClass) { root.classList.remove(themeClass); }
          });
          if (tab.dataset.versusTheme) { root.classList.add(tab.dataset.versusTheme); }
          if (focusTab) {
            try { tab.focus({ preventScroll: true }); }
            catch (_) { tab.focus(); }
          }
          keepActiveTabVisible(tab);
        }
      });

      panels.forEach(function (panel, panelIndex) {
        var selected = panelIndex === activeIndex;
        panel.hidden = !selected;
        panel.classList.toggle('is-active', selected);
      });

      if (status) {
        var statusSource = tabs[activeIndex] ||
          panels[activeIndex].querySelector('.sb-versus__product-title');
        var statusLabel = '';
        if (statusSource) {
          /* Tagline-carrying tabs hold both spans in textContent; the
             aria-label carries the clean "tagline · product" reading. */
          statusLabel = (statusSource.getAttribute('aria-label') || statusSource.textContent).trim();
        }
        var ofLabel = status.getAttribute('data-versus-of') || 'of';
        status.textContent = statusLabel + ', ' + (activeIndex + 1) + ' ' + ofLabel + ' ' + count;
      }

      if (previous) { previous.disabled = activeIndex === 0; }
      if (next) { next.disabled = activeIndex === count - 1; }

      updateIndicator();
      window.requestAnimationFrame(positionPan);
    }

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () { select(index, false); });
      tab.addEventListener('keydown', function (event) {
        var target = null;

        if (event.key === 'ArrowRight') { target = (index + 1) % count; }
        else if (event.key === 'ArrowLeft') { target = (index - 1 + count) % count; }
        else if (event.key === 'Home') { target = 0; }
        else if (event.key === 'End') { target = count - 1; }

        if (target !== null) {
          event.preventDefault();
          select(target, true);
        }
      });
    });

    if (previous) {
      previous.addEventListener('click', function () { select(activeIndex - 1, false); });
    }
    if (next) {
      next.addEventListener('click', function () { select(activeIndex + 1, false); });
    }

    media.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        select(activeIndex - 1, false);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        select(activeIndex + 1, false);
      } else if (event.key === 'Home') {
        event.preventDefault();
        select(0, false);
      } else if (event.key === 'End') {
        event.preventDefault();
        select(count - 1, false);
      }
    });

    var pointerId = null;
    var startX = 0;
    var startY = 0;

    media.addEventListener('pointerdown', function (event) {
      if (!event.isPrimary || event.target.closest('button')) { return; }
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      try { media.setPointerCapture(pointerId); } catch (_) { /* Best effort. */ }
    });

    function finishSwipe(event) {
      if (pointerId === null || event.pointerId !== pointerId) { return; }

      var deltaX = event.clientX - startX;
      var deltaY = event.clientY - startY;
      pointerId = null;

      if (Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY)) { return; }
      select(activeIndex + (deltaX < 0 ? 1 : -1), false);
    }

    media.addEventListener('pointerup', finishSwipe);
    media.addEventListener('pointercancel', function () { pointerId = null; });

    function onResize() {
      positionPan();
      updateIndicator();
    }

    if ('ResizeObserver' in window) {
      var resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(media);
      if (tablist) { resizeObserver.observe(tablist); }
    } else {
      window.addEventListener('resize', onResize, { passive: true });
    }

    if (pan.complete) { positionPan(); }
    else { pan.addEventListener('load', positionPan, { once: true }); }

    select(0, false);
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-sb-versus]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-sb-versus]');
    if (root) {
      root.dataset.sbVersusReady = '';
      init(root);
    }
  });
})();
